import {
  type CanvasElementFrom,
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import { type FontWeightOption } from '@blaze-shared/api-contracts-domain';
import {
  type ElementSizingMode,
  type PropertiesConfig,
  type PropertiesConfigItemDefinition,
  SLIDER_FORMAT_OPTIONS,
} from '@blaze-shared/elements-domain';
import { type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';

import { FormLikeFieldValidationHandler } from '../../canvas-elements/io-data/default-handlers/formlike-field-validation-handler.service';
import { registerElementDefinition } from '../../services/element-definitions.service';
import {
  validateFontSizeWithLineHeight,
  validateLineHeightWithFontSize,
} from '../../utils/elements/validation.utils';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { generateSliderFormField } from './generation/form-field';
import { sliderV2PaddingToMarginMigration } from './migrations/002-slider-padding-to-margin.migration';
import { SliderComponent } from './slider.component';
import { type SliderPropsModel } from './slider.models';
import { SliderPropsBuilder } from './slider.props-builder';
import { clampHandleSize, clampTrackWeight } from './slider.utils';

export type SliderElement = CanvasElementFrom<'slider'>;
export type SliderDef = WithProps<SliderPropsModel> & WithIoConfig;

declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    slider: SliderDef;
  }
}

const DEFAULT_WIDTH = 356;
const DEFAULT_HEIGHT = 48;
const MIN_SIZE = 40;
const MAX_WIDTH = 900;
const MAX_HEIGHT = 150;
const MAX_HANDLE_SIZE = 48;
const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full'];
const HEIGHT_OPTIONS: ElementSizingMode[] = ['fixed', 'auto'];

registerElementDefinition({
  type: 'slider',
  componentType: SliderComponent,
  validationHandler: FormLikeFieldValidationHandler,
  isListed: true,
  name: 'Slider',
  width: { mode: 'value', value: DEFAULT_WIDTH, fixed: true },
  height: { mode: 'value', value: DEFAULT_HEIGHT, fixed: false },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  defaults: getDefaults(),
  initial: getInitial(),
  propsBuilder: SliderPropsBuilder,
  generation: {
    formField: generateSliderFormField,
  },
  groups: ['custom'],
  styleIncludePaths: [
    'style',
    'content.title.align',
    'content.title.iconPosition',
    'content.title.position',
    'content.title.width',
  ],
  metaUiFeatures: {
    type: 'input',
    paths: {
      format: 'content.options.format',
      precision: 'content.options.precision',
      currency: 'content.options.currency',
      readonly: 'content.options.readonly',
    },
  },
  features: {
    ai: {
      optionalInput: true,
    },
  },
  version: 2,
  migrations: {
    2: sliderV2PaddingToMarginMigration,
  },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [{ property: 'value', label: 'Value', dataType: 'Number' }],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [
      {
        eventType: 'change',
        debounce: 300,
        schema: {
          columns: [
            {
              columnType: 'Number',
              dataType: 'Number',
              isProfilePicture: false,
              name: '',
            },
          ],
          id: '',
          name: 'Slider',
        },
      },
    ],
  };
}

function getDefaults(): PropsOf<'slider'> {
  return {
    content: {
      title: {
        label: 'Label',
        position: 'left',
        align: 'left',
        width: '30%',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
      options: {
        format: 'number',
        min: 0,
        max: 100,
        defaultValue: 0,
        step: 10,
        precision: 0,
        currency: 'USD',
        readonly: false,
      },
      display: {
        valuePosition: 'side',
        showRangeLabels: true,
        showTickMarks: false,
        showTickLabels: false,
      },
    },
    style: {
      fontSize: '$t.fontSize',
      fontFamily: '$t.fontFamily',
      lineHeight: '$t.lineHeight',
      general: {
        title: { color1: '$t.title', selectorType: 'solid' },
        accent: { color1: '$t.accent', selectorType: 'solid' },
        offset: { margin: '$t.spacingInput' },
      },
      labelBlock: {
        fontFamily: '$t.fontFamily',
        fontSize: '$t.fontSize',
        fontStyle: [],
        fontWeight: '$t.labelFontWeight',
        iconSize: '16px',
        lineHeight: '$t.lineHeight',
      },
      handle: {
        size: '15px',
        fill: { color1: '$t.accent', selectorType: 'solid' },
        outline: { color1: '$t.fill', selectorType: 'solid' },
        outlineWidth: '1px',
      },
      track: {
        weight: '4px',
        active: { color1: '$t.accent', selectorType: 'solid' },
        inactive: { color1: '$t.complementary', selectorType: 'solid' },
      },
      ticks: {
        color: { color1: '$t.value_a50', selectorType: 'solid' },
      },
      value: {
        color: { color1: '$t.value', selectorType: 'solid' },
        font: {
          style: [],
          fontFamily: '$t.fontFamily',
          fontWeight: '$t.fontWeight' as FontWeightOption,
          lineHeight: '$t.lineHeight',
          fontSize: '$t.fontSize',
        },
      },
      limits: {
        color: { color1: '$t.value_a50', selectorType: 'solid' },
        font: {
          style: [],
          fontFamily: '$t.fontFamily',
          fontWeight: '$t.fontWeight' as FontWeightOption,
          lineHeight: '$t.lineHeight',
          fontSize: '11px',
        },
      },
      tickLabels: {
        color: { color1: '$t.value_a50', selectorType: 'solid' },
        font: {
          style: [],
          fontFamily: '$t.fontFamily',
          fontWeight: '$t.fontWeight' as FontWeightOption,
          lineHeight: '$t.lineHeight',
          fontSize: '11px',
        },
      },
    },
  };
}

function getInitial(): PartialPropsOf<'slider'> {
  return {
    content: {
      title: {
        width: '35%',
      },
    },
  };
}

function fontSizeLineHeightFields(
  fontSizePath: string,
  lineHeightPath: string,
): PropertiesConfigItemDefinition<'slider'>[] {
  return [
    epc.utils.observedField(
      epc.fields.sliderField(fontSizePath, 'Size', [{ min: 8, max: 72, unit: 'px' }]),
      (props: SliderPropsModel, value: ValueWithUnitOfMeasure) =>
        validateLineHeightWithFontSize(props, value, lineHeightPath),
    ),
    epc.utils.observedField(
      epc.fields.sliderField(lineHeightPath, 'Line height', [{ min: 8, max: 72, unit: 'px' }]),
      (props: SliderPropsModel, value: ValueWithUnitOfMeasure) =>
        validateFontSizeWithLineHeight(props, value, fontSizePath),
    ),
  ];
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('slider', [
    epc.section('content', 'Content', [
      epc.subsections.subsection(
        'content.options',
        'Options',
        [
          epc.fields.selectFieldFromList(
            'content.options.format',
            'Format',
            SLIDER_FORMAT_OPTIONS,
            {
              allowNone: false,
              showSelection: true,
              showNoneOption: false,
              defaultValue: 'number',
            },
          ),
          epc.utils.conditionalField(
            epc.fields.numberPrecisionField('content.options.precision'),
            'content.options.format',
            (format) => format === 'integer',
          ),
          epc.fields.currencyConditionalField('content.options.currency', 'content.options.format'),
          epc.fields.textInput('content.options.min', 'Min value', { inputType: 'number' }),
          epc.fields.textInput('content.options.max', 'Max value', { inputType: 'number' }),
          epc.fields.textInput('content.options.defaultValue', 'Default value', {
            inputType: 'number',
          }),
          epc.fields.textInput('content.options.step', 'Step', { inputType: 'number' }),
          epc.fields.toggleField('content.options.readonly', 'Read Only'),
        ],
        {
          dataBindings: [
            {
              key: 'value',
              label: 'Value',
              sources: 'field',
              bindings: [{ property: 'value', label: 'Value', dataType: 'Number' }],
            },
          ],
        },
      ),

      epc.subsections.subsection('content.display', 'Display', [
        epc.fields.radiosField('content.display.valuePosition', 'Value position', [
          { value: 'side', label: 'Side' },
          { value: 'above', label: 'Above' },
        ]),
        epc.fields.toggleField('content.display.showRangeLabels', 'Range labels'),
        epc.fields.toggleField('content.display.showTickMarks', 'Tick marks'),
        epc.fields.toggleField('content.display.showTickLabels', 'Tick labels'),
      ]),

      epc.subsections.elementSizeSubsection(
        MIN_SIZE,
        MAX_WIDTH,
        MAX_HEIGHT,
        WIDTH_OPTIONS,
        HEIGHT_OPTIONS,
      ),

      epc.subsections.visibilitySubsection(),
      epc.subsections.labelContentSubsection('content'),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.general', 'Margin', [
          epc.fields.spacingAndAlignField('style.general.offset', {
            hasMargin: true,
          }),
        ]),

        epc.subsections.subsection('style.handle', 'Handle', [
          epc.fields.sliderField('style.handle.size', 'Size', [
            { min: 8, max: MAX_HANDLE_SIZE, unit: 'px' },
          ]),
          epc.fields.colorField('style.handle.fill', 'Fill', {
            allowNone: false,
            allowGradient: false,
          }),
          epc.fields.colorField('style.handle.outline', 'Outline', { allowGradient: false }),
          epc.fields.sliderField('style.handle.outlineWidth', 'Width', [
            { min: 0, max: 8, unit: 'px' },
          ]),
        ]),

        epc.subsections.subsection('style.track', 'Track', [
          epc.utils.observedField(
            epc.fields.sliderField('style.track.weight', 'Weight', [
              { min: 1, max: MAX_HANDLE_SIZE / 2, unit: 'px' },
            ]),
            (model: SliderPropsModel, value: string) => {
              const handleSize = clampHandleSize(
                Number.parseInt(model.style.handle.size, 10) || 20,
              );
              const weight = clampTrackWeight(Number.parseInt(value, 10) || 4, handleSize);
              if (`${weight}px` !== value) {
                return { 'style.track.weight': `${weight}px` };
              }
              return {};
            },
          ),
          epc.fields.colorField('style.track.active', 'Active', {
            allowNone: false,
            allowGradient: false,
          }),
          epc.fields.colorField('style.track.inactive', 'Inactive', {
            allowNone: false,
            allowGradient: false,
          }),
        ]),

        epc.subsections.subsection(
          'style.ticks',
          'Ticks',
          [epc.fields.colorField('style.ticks.color', 'Color', { allowGradient: false })],
          { hideWhen: (_, __, model) => !model?.content?.display?.showTickMarks },
        ),

        epc.subsections.subsection('style.value', 'Value', [
          epc.fields.colorField('style.value.color', 'Color', {
            allowGradient: false,
            allowNone: false,
          }),
          ...epc.fields.fontFamilyAndWeightFields(
            'style.value.font.fontFamily',
            'style.value.font.fontWeight',
          ),
          epc.fields.fontStyleField('style.value.font.style', 'Style', ['bold', 'line-through']),
          ...fontSizeLineHeightFields('style.value.font.fontSize', 'style.value.font.lineHeight'),
        ]),

        epc.subsections.subsection(
          'style.limits',
          'Limits',
          [
            epc.fields.colorField('style.limits.color', 'Color', {
              allowGradient: false,
              allowNone: false,
            }),
            ...epc.fields.fontFamilyAndWeightFields(
              'style.limits.font.fontFamily',
              'style.limits.font.fontWeight',
            ),
            epc.fields.fontStyleField('style.limits.font.style', 'Style', ['bold', 'line-through']),
            ...fontSizeLineHeightFields(
              'style.limits.font.fontSize',
              'style.limits.font.lineHeight',
            ),
          ],
          { hideWhen: (_, __, model) => !model?.content?.display?.showRangeLabels },
        ),

        epc.subsections.subsection(
          'style.tickLabels',
          'Tick labels',
          [
            epc.fields.colorField('style.tickLabels.color', 'Color', {
              allowGradient: false,
              allowNone: false,
            }),
            ...epc.fields.fontFamilyAndWeightFields(
              'style.tickLabels.font.fontFamily',
              'style.tickLabels.font.fontWeight',
            ),
            ...fontSizeLineHeightFields(
              'style.tickLabels.font.fontSize',
              'style.tickLabels.font.lineHeight',
            ),
          ],
          { hideWhen: (_, __, model) => !model?.content?.display?.showTickLabels },
        ),

        epc.subsections.labelStyleSubsection({
          color: 'style.general.title',
          enabled: 'content.title.enabled',
          fontFamily: 'style.labelBlock.fontFamily',
          fontSize: 'style.labelBlock.fontSize',
          fontStyle: 'style.labelBlock.fontStyle',
          fontWeight: 'style.labelBlock.fontWeight',
          horizontalAlign: 'content.title.align',
          icon: 'content.title.icon',
          iconColor: 'content.title.iconColor',
          iconPosition: 'content.title.iconPosition',
          iconSize: 'style.labelBlock.iconSize',
          lineHeight: 'style.labelBlock.lineHeight',
          position: 'content.title.position',
          width: 'content.title.width',
        }),
      ],
      true,
    ),
  ]);
}
